import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';
import { registerClearable } from '../utils/cacheRegistry';

// Axios instance for delivery challans API
const deliveryChallanAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
deliveryChallanAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
deliveryChallanAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const BASE_URL = '/delivery-challans';

// Caching helpers (memory + sessionStorage)
const CACHE_PREFIX = 'erp_delivery_challans_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

// API Methods
const deliveryChallanService = {
  // Get all delivery challans
  async getAllDeliveryChallans(options = {}) {
    const { page = 1, limit = 100, force = false } = options;
    const key = `all_delivery_challans_${page}_${limit}`;

    if (!force) {
      const cached = readCache(key) || readMemoryCache(key);
      if (cached) return { success: true, data: cached };
    }

    try {
      const response = await deliveryChallanAPI.get(`${BASE_URL}`);
      const data = response.data;

      writeCache(key, data);
      writeMemoryCache(key, data);

      return { success: true, data };
    } catch (error) {
      console.error('Failed to fetch delivery challans:', error);
      const errorMsg = extractApiError(error);
      return { success: false, error: errorMsg };
    }
  },

  // (updateDeliveryChallan removed: PUT /delivery-challans/{id} was a legacy
  // order-level route the backend no longer serves; edits go through
  // deliveryOrderService.editChallan.)

  async getUnprinted(params = {}) {
    try {
      const response = await deliveryChallanAPI.get('/delivery-challans/unprinted', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async markChallanPrinted(subOrderId) {
    try {
      const response = await deliveryChallanAPI.post(`/delivery-sub-orders/${subOrderId}/mark-challan-printed`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Drop every cached entry for this service.
   *
   * Deleting an order elsewhere (e.g. the Proforma Invoice screen's bulk delete, which
   * goes through ordersService) leaves these lists holding rows the server no longer
   * has. Without this the next fetch is served from sessionStorage for the rest of the
   * TTL and the deleted records reappear.
   */
  clearCache() {
    try {
      memoryCache.clear();
    } catch (e) { /* non-fatal */ }
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) { /* sessionStorage unavailable (private mode / SSR) */ }
  },
};

// Cleared on logout together with the other services (policy C16).
registerClearable(() => deliveryChallanService.clearCache());

export default deliveryChallanService;